/*
 * Outfit Composer engine
 * -----------------------------------------------------------------------------
 * A standalone image-composition engine for the "outfit-composer" section.
 * Independent of the color-palette outfit system (color-palette-outfits.js /
 * palette-outfit-assets.json). It:
 *
 *   1. Optionally re-randomizes the model layer client-side (for anonymous
 *      visitors, when the server render may be cached).
 *   2. Recolors "variant-linked" garment parts to the selected variant's color
 *      on a product page, and keeps them in sync as the shopper switches
 *      variants. Color -> hex is resolved from a color catalog (the inline
 *      Abuja catalog by default, or a fetched catalog JSON).
 *
 * Recolor strategies per part (data attributes on the <img>):
 *   - data-variant-template : per-color URL template with {colorSlug}/{vendorCode}
 *   - data-master           : master plate URL recolored client-side (default) or via
 *                             Cloudinary e_tint when data-recolor-mode="cloudinary"
 *
 * Default recolor (data-recolor-mode="luminance", or unset) mirrors the export
 * pipeline's luminance-preserving multiply (collage._recolor_literal_fill_keep_shading)
 * so storefront cards/PDP match exported Shopify composites more closely than
 * Cloudinary e_grayscale/e_tint.
 *
 * Layer transparency: ``data-alpha`` (from ``custom.outfit_parts``) mirrors the
 * export collage fill alpha (e.g. Calceta chiffon shell ``transparency_alpha``).
 * Applied as CSS ``opacity`` on each part ``<img>``.
 *
 * Roots are any element matching [data-outfit-composer]. Config is read from
 * data-* attributes so the engine has no hard dependency on Liquid internals.
 */
(function () {
  'use strict';

  // The engine may be referenced from multiple places (PDP snippet + one tag per
  // product card). Run the setup only once per page.
  if (window.__kailaOutfitComposerLoaded) return;
  window.__kailaOutfitComposerLoaded = true;

  var CATALOG_URL_CACHE = {};
  var COMPLEMENTS_URL_CACHE = {};
  // masterUrl|hex -> data URL (luminance recolor)
  var RECOLOR_CACHE = {};
  var DENIM_FALLBACK_HEXES = ['#3a3a3a', '#5e4237', '#625f50', '#848388', '#2c2c2c'];

  // Hide until every layer has finished loading so shoppers never see a partial
  // outfit (model in underwear, then garments appearing one by one).
  (function injectRevealStyles() {
    if (document.getElementById('oc-reveal-styles')) return;
    var style = document.createElement('style');
    style.id = 'oc-reveal-styles';
    style.textContent =
      '[data-outfit-composer].is-oc-loading{opacity:0}' +
      '[data-outfit-composer].is-oc-ready{opacity:1;transition:opacity .15s ease}';
    document.head.appendChild(style);
  })();

  function composerLayers(root) {
    return root.querySelectorAll('img');
  }

  function revealRoot(root) {
    root.classList.remove('is-oc-loading');
    root.classList.add('is-oc-ready');
    root.setAttribute('data-oc-ready', 'true');
    root.removeAttribute('aria-hidden');
  }

  function waitForLayers(root, callback) {
    var list = Array.prototype.slice.call(composerLayers(root));
    if (!list.length) {
      callback();
      return;
    }
    var timeout = parseInt(root.getAttribute('data-oc-reveal-timeout') || '6000', 10);
    var pending = 0;
    var finished = false;
    function done() {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      callback();
    }
    var timer = setTimeout(done, timeout);

    list.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      pending++;
      function settle() {
        img.removeEventListener('load', settle);
        img.removeEventListener('error', settle);
        pending--;
        if (pending <= 0) done();
      }
      img.addEventListener('load', settle);
      img.addEventListener('error', settle);
    });

    if (pending === 0) done();
  }

  function whenNearViewport(root, fn) {
    var rect = root.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight)) {
      fn();
      return;
    }
    if (typeof IntersectionObserver !== 'function') {
      fn();
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        fn();
      });
    }, { rootMargin: '240px 0px' });
    obs.observe(root);
  }

  function scheduleReveal(root) {
    if (root.getAttribute('data-oc-ready') === 'true') return;
    var gen = (root._ocRevealGen = (root._ocRevealGen || 0) + 1);
    // Listing cards set data-oc-context="card"; PDPs also have data-color-seed
    // so seed presence must not be used as the card flag.
    var isCard = root.getAttribute('data-oc-context') === 'card';
    function startWait() {
      if (root._ocRevealGen !== gen || root.getAttribute('data-oc-ready') === 'true') return;
      waitForLayers(root, function () {
        if (root._ocRevealGen !== gen || root.getAttribute('data-oc-ready') === 'true') return;
        revealRoot(root);
      });
    }
    // Cards use eager loading; don't defer reveal until scroll.
    if (isCard) {
      startWait();
    } else {
      whenNearViewport(root, startWait);
    }
  }

  function toBool(value) {
    return String(value) === 'true';
  }

  function parseSlugs(value) {
    return String(value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* ---- Color catalog ------------------------------------------------------ */

  function buildColorMaps(colors) {
    var byName = {};
    var byCode = {};
    (colors || []).forEach(function (c) {
      if (!c) return;
      if (c.name) byName[String(c.name).toLowerCase()] = c;
      if (c.slug) {
        byName[String(c.slug).toLowerCase()] = c;
        byName[String(c.slug).toLowerCase().replace(/[-_]+/g, ' ')] = c;
      }
      if (c.vendorCode) byCode[String(c.vendorCode)] = c;
    });
    // Alternate storefront display names (e.g. "Aloe Green" -> Aloe) resolve like the
    // primary name — in a second pass so they win over another row's catalog name:
    // the shop's "Oat" / "Olive" are Agate Beige / Forest Green, not the catalog
    // rows named Oat / Olive (2026-10-31).
    (colors || []).forEach(function (c) {
      if (!c) return;
      (c.aliases || []).forEach(function (alias) {
        if (alias) byName[String(alias).toLowerCase()] = c;
        var cl = String(alias).match(/^cl0*(\d+)$/i);
        if (cl) byCode[cl[1]] = c;
      });
    });
    return { byName: byName, byCode: byCode };
  }

  function readInlineCatalog() {
    var el = document.getElementById('AbujaPaletteCatalog');
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent);
      return (parsed && parsed.colors) || null;
    } catch (e) {
      return null;
    }
  }

  function getCatalogColors(root) {
    var inline = readInlineCatalog();
    if (inline && inline.length) return Promise.resolve(inline);

    var primary = root.getAttribute('data-catalog-url');
    var fallback = root.getAttribute('data-catalog-fallback');
    if (!primary && !fallback) return Promise.resolve([]);

    function fetchCatalog(url) {
      if (!url) return Promise.resolve([]);
      if (CATALOG_URL_CACHE[url]) return CATALOG_URL_CACHE[url];
      var request = fetch(url)
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) { return (data && data.colors) || []; })
        .catch(function () { return []; });
      CATALOG_URL_CACHE[url] = request;
      return request;
    }

    return fetchCatalog(primary).then(function (colors) {
      if (colors && colors.length) return colors;
      return fetchCatalog(fallback);
    });
  }

  function normalizeHexKey(hex) {
    var h = String(hex || '')
      .trim()
      .toLowerCase()
      .replace(/^#/, '');
    return h ? '#' + h : '';
  }

  function getCompanionComplements(root) {
    var url = (root.getAttribute('data-complements-url') || '').trim();
    if (!url) return Promise.resolve(null);
    if (COMPLEMENTS_URL_CACHE[url]) return COMPLEMENTS_URL_CACHE[url];
    var request = fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('complements ' + r.status);
        return r.json();
      })
      .catch(function () {
        return null;
      });
    COMPLEMENTS_URL_CACHE[url] = request;
    return request;
  }

  function companionLayerIsJeans(img) {
    if (!img) return false;
    var hint =
      (img.getAttribute('data-master') || '') +
      ' ' +
      (img.getAttribute('src') || '') +
      ' ' +
      (img.getAttribute('alt') || '');
    return /(?:^|\/)jeans(?:\.png)?(?:\?|$)/i.test(hint) || /\/jeans\./i.test(hint);
  }

  function pickCompanionHexForProduct(productColor, complements, preferJeans) {
    if (!productColor) return null;
    var hexKey = normalizeHexKey(productColor.hex);
    var nameKey = String(productColor.name || '')
      .trim()
      .toLowerCase();
    var pool = [];
    if (complements) {
      if (preferJeans && complements.jeansByHex && complements.jeansByHex[hexKey]) {
        pool = complements.jeansByHex[hexKey].slice();
      } else if (complements.byHex && complements.byHex[hexKey]) {
        pool = complements.byHex[hexKey].slice();
      } else if (complements.byName && nameKey && complements.byName[productColor.name]) {
        pool = complements.byName[productColor.name].slice();
      } else if (complements.byName) {
        var names = Object.keys(complements.byName);
        for (var i = 0; i < names.length; i++) {
          if (String(names[i]).trim().toLowerCase() === nameKey) {
            pool = complements.byName[names[i]].slice();
            break;
          }
        }
      }
    }
    if (preferJeans && (!pool || !pool.length)) {
      pool = DENIM_FALLBACK_HEXES.slice();
    }
    if (!pool || !pool.length) return null;
    // Never tint the companion to the same hex as the product shell.
    var filtered = pool.filter(function (h) {
      return normalizeHexKey(h) && normalizeHexKey(h) !== hexKey;
    });
    if (!filtered.length) filtered = pool;
    var pick = filtered[Math.floor(Math.random() * filtered.length)];
    return normalizeHexKey(pick).replace(/^#/, '');
  }

  function resolveColor(value, maps) {
    if (!value) return null;
    var v = String(value).trim().toLowerCase();
    if (maps.byName[v]) return maps.byName[v];

    // Taxonomy / handle form: "forest-green" -> "forest green".
    var spaced = v.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (maps.byName[spaced]) return maps.byName[spaced];

    var m = v.match(/^abu[-_ ]?0*(\d+)$/);
    if (m) {
      if (maps.byCode[m[1]]) return maps.byCode[m[1]];
      var padded = ('000' + m[1]).slice(-3);
      if (maps.byCode[padded]) return maps.byCode[padded];
    }
    if (maps.byCode[v]) return maps.byCode[v];
    // Bare numeric vendor code, possibly zero-padded ("13" -> "013").
    if (/^\d+$/.test(v)) {
      var pad = ('000' + v).slice(-3);
      if (maps.byCode[pad]) return maps.byCode[pad];
    }

    // Multi-word display names where the catalog key is only part of the name
    // (e.g. "Deep Plum" -> "plum", "Aloe Green" -> "aloe", "Ice Blue" -> "ice").
    // Try the longest contiguous word groups first, then shorter ones.
    var tokens = spaced.split(' ');
    if (tokens.length > 1) {
      for (var len = tokens.length - 1; len >= 1; len--) {
        for (var start = 0; start + len <= tokens.length; start++) {
          var gram = tokens.slice(start, start + len).join(' ');
          if (maps.byName[gram]) return maps.byName[gram];
        }
      }
    }
    return null;
  }

  /* ---- URL helpers -------------------------------------------------------- */

  function colorSlug(name) {
    return String(name || '')
      .trim()
      .split(/\s+/)[0]
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  // Delivery prefix: resize + auto format/quality so Cloudinary generates a
  // small derivative fast (masters are 2048x2048; the composer renders ~<=600px).
  // Use PNG (not f_auto) for luminance recolor so canvas gets lossless RGBA + CORS.
  // Cards crop to 3:4 (same as hover videos) so still + video share framing.
  var CARD_FRAME = 'ar_3:4,c_fill,g_center';
  var TINT_DELIVERY_PREFIX = 'f_auto,q_auto,w_1200,c_limit';
  var TINT_DELIVERY_PREFIX_CARD = 'f_auto,q_auto,w_800,' + CARD_FRAME;
  var MASTER_DELIVERY_PREFIX = 'f_png,w_1200,c_limit';
  var MASTER_DELIVERY_PREFIX_CARD = 'f_png,w_800,' + CARD_FRAME;

  function withUploadTransform(master, transform) {
    if (!master || master.indexOf('/upload/') === -1 || !transform) return master;
    var marker = '/upload/';
    var i = master.indexOf(marker) + marker.length;
    var rest = master.slice(i);
    // Avoid stacking the same prefix repeatedly.
    if (rest.indexOf(transform + '/') === 0) return master;
    return master.slice(0, i) + transform + '/' + rest;
  }

  function isCardComposerRoot(root) {
    return !!(root && root.getAttribute && root.getAttribute('data-oc-context') === 'card');
  }

  function composerRootForNode(node) {
    return node && node.closest ? node.closest('[data-outfit-composer]') : null;
  }

  function tintUrl(master, hex, intensity, grayscale, opts) {
    if (!master || master.indexOf('/upload/') === -1 || !hex) return master;
    var h = String(hex).replace(/^#/, '').toLowerCase();
    var prefix =
      opts && opts.card ? TINT_DELIVERY_PREFIX_CARD : TINT_DELIVERY_PREFIX;
    var transform =
      prefix + '/' + (grayscale ? 'e_grayscale/' : '') + 'e_tint:' + intensity + ':rgb:' + h;
    return withUploadTransform(master, transform);
  }

  function masterDeliveryUrl(master, opts) {
    var prefix =
      opts && opts.card ? MASTER_DELIVERY_PREFIX_CARD : MASTER_DELIVERY_PREFIX;
    return withUploadTransform(master, prefix);
  }

  function parseHexRgb(hex) {
    var h = String(hex || '').replace(/^#/, '').toLowerCase();
    if (h.length === 3) {
      h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
    }
    if (h.length !== 6) return null;
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16)
    ];
  }

  /**
   * Export-matched luminance multiply (see collage._recolor_literal_fill_keep_shading).
   * Keeps fold shading from the source plate instead of Cloudinary grayscale+tint.
   */
  function luminanceRecolorImageData(imageData, hex) {
    var rgb = parseHexRgb(hex);
    if (!rgb) return;
    var tr = rgb[0];
    var tg = rgb[1];
    var tb = rgb[2];
    var d = imageData.data;
    var tgtLum = (0.299 * tr + 0.587 * tg + 0.114 * tb) / 255.0;
    var lumCurve = 1.22;
    var shadeFloor = 0.14;
    var srcMix = 0.16;
    if (tgtLum >= 0.9) {
      shadeFloor = 0.64;
      srcMix = 0.1;
    } else if (tgtLum <= 0.12) {
      shadeFloor = 0.065;
      srcMix = 0.08;
    }
    for (var i = 0; i < d.length; i += 4) {
      var a = d[i + 3];
      if (a === 0) continue;
      // Match export ``_knock_out_opaque_white_paper``: drop opaque near-white
      // paper so hem/edge halos are not tinted into a light fringe.
      if (a > 200 && d[i] >= 245 && d[i + 1] >= 245 && d[i + 2] >= 245) {
        d[i + 3] = 0;
        continue;
      }
      var lum = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255.0;
      // Soft anti-alias fringe (bright, low-alpha): pull toward target and
      // tighten alpha so companion fills don't read as white outlines.
      if (a < 220 && lum >= 0.78) {
        var fringeKeep = Math.max(0.15, (1.0 - lum) * 2.2);
        a = Math.round(a * fringeKeep);
        d[i + 3] = a;
        if (a === 0) continue;
        d[i] = tr;
        d[i + 1] = tg;
        d[i + 2] = tb;
        continue;
      }
      var lumBoost = (lum - 0.5) * lumCurve + 0.5;
      if (lumBoost < 0) lumBoost = 0;
      else if (lumBoost > 1) lumBoost = 1;
      var shade = shadeFloor + (1.0 - shadeFloor) * lumBoost;
      if (shade < 0) shade = 0;
      else if (shade > 1) shade = 1;
      var nr = tr * shade;
      var ng = tg * shade;
      var nb = tb * shade;
      // Near-black targets: don't blend source paper highlights back in (halo).
      var mix = srcMix;
      if (tgtLum <= 0.12 && lum >= 0.72) mix = 0;
      d[i] = Math.round((1.0 - mix) * nr + mix * d[i]);
      d[i + 1] = Math.round((1.0 - mix) * ng + mix * d[i + 1]);
      d[i + 2] = Math.round((1.0 - mix) * nb + mix * d[i + 2]);
    }
  }

  function luminanceRecolorToDataUrl(sourceUrl, hex, done) {
    var key = String(sourceUrl || '') + '|' + String(hex || '').toLowerCase();
    if (RECOLOR_CACHE[key]) {
      done(RECOLOR_CACHE[key]);
      return;
    }
    if (!sourceUrl || !hex) {
      done(null);
      return;
    }
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = im.naturalWidth || im.width;
        canvas.height = im.naturalHeight || im.height;
        if (!canvas.width || !canvas.height) {
          done(null);
          return;
        }
        var ctx = canvas.getContext('2d');
        ctx.drawImage(im, 0, 0);
        var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        luminanceRecolorImageData(imageData, hex);
        ctx.putImageData(imageData, 0, 0);
        var url = canvas.toDataURL('image/png');
        RECOLOR_CACHE[key] = url;
        done(url);
      } catch (e) {
        // Tainted canvas (CORS) — caller falls back to Cloudinary tint.
        done(null);
      }
    };
    im.onerror = function () {
      done(null);
    };
    im.src = sourceUrl;
  }

  function partUrl(img, color, opts) {
    var template = img.getAttribute('data-variant-template');
    if (template && String(template).trim()) {
      var url = template
        .replace('{colorSlug}', colorSlug(color.name))
        .replace('{vendorCode}', color.vendorCode || '');
      // Card templates are often bare upload URLs — apply 3:4 frame.
      if (opts && opts.card && url.indexOf('/upload/') !== -1) {
        url = withUploadTransform(url, TINT_DELIVERY_PREFIX_CARD);
      }
      return url;
    }
    var master = img.getAttribute('data-master');
    if (!master) return '';
    // Cloudinary fallback URL (also used when recolor-mode is cloudinary).
    return tintUrl(master, color.hex, opts.intensity, opts.grayscale, {
      card: !!(opts && opts.card),
    });
  }

  function applyColorToLayers(layers, color, opts, done) {
    var list = Array.prototype.slice.call(layers || []);
    if (!list.length) {
      if (done) done();
      return;
    }
    var mode = (opts && opts.recolorMode) || 'luminance';
    var card =
      !!(opts && opts.card) ||
      isCardComposerRoot(composerRootForNode(list[0]));
    var layerOpts = {
      intensity: opts && opts.intensity,
      grayscale: opts && opts.grayscale,
      recolorMode: mode,
      card: card,
    };
    var pending = list.length;
    function settle() {
      pending--;
      if (pending <= 0 && done) done();
    }
    list.forEach(function (img) {
      // Companion layers use data-fixed-hex; never overwrite with the product color.
      if (img.hasAttribute('data-fixed-hex')) {
        settle();
        return;
      }
      img._ocWantHex = color.hex;
      var template = (img.getAttribute('data-variant-template') || '').trim();
      if (template) {
        var templated = partUrl(img, color, layerOpts);
        if (templated && img.src !== templated) img.src = templated;
        settle();
        return;
      }
      var master = img.getAttribute('data-master');
      if (!master) {
        settle();
        return;
      }
      if (mode === 'cloudinary') {
        var curl = tintUrl(master, color.hex, opts.intensity, opts.grayscale, {
          card: card,
        });
        if (curl && img.src !== curl) img.src = curl;
        settle();
        return;
      }
      // Luminance path (export-matched). Card masters are pre-cropped 3:4.
      luminanceRecolorToDataUrl(
        masterDeliveryUrl(master, { card: card }),
        color.hex,
        function (dataUrl) {
          if (img._ocWantHex !== color.hex) {
            settle();
            return;
          }
          if (dataUrl) {
            if (img.src !== dataUrl) img.src = dataUrl;
          } else {
            var fallback = tintUrl(master, color.hex, opts.intensity, opts.grayscale, {
              card: card,
            });
            if (fallback && img.src !== fallback) img.src = fallback;
          }
          settle();
        }
      );
    });
  }

  /* ---- Variant selection -------------------------------------------------- */

  function scopeFor(el) {
    return (
      el.closest('product-info, .product, variant-selects, form[action*="/cart/add"]') ||
      document
    );
  }

  function candidateValuesFromNode(node) {
    // Gather every string that might name the color, in priority order.
    var vals = [];
    if (node.value) vals.push(node.value);
    if (node.textContent) vals.push(node.textContent);
    ['data-value', 'data-option-value', 'aria-label', 'title'].forEach(function (attr) {
      var a = node.getAttribute && node.getAttribute(attr);
      if (a) vals.push(a);
    });
    // Checked swatch inputs often carry their label text elsewhere.
    if (node.id) {
      var lbl = document.querySelector('label[for="' + node.id + '"]');
      if (lbl && lbl.textContent) vals.push(lbl.textContent);
    }
    return vals.map(function (s) { return String(s).trim(); }).filter(Boolean);
  }

  // Variant controls that belong to another option (Size-1, options[Fit], …). Their values
  // can look like color codes — "Regular (164–172 cm)" resolved to color 164 — so skip them.
  var COLOR_OPTION_NAME_RE = /(color|colour|farbe|couleur|kleur|colore)/i;
  function isOtherOptionControl(node) {
    var ctl = node && node.closest ? node.closest('input, select') : null;
    // Dawn renders names with a trailing newline ("Size-1\n").
    var name = ctl && String(ctl.getAttribute('name') || '').trim();
    if (!name) return false;
    var isVariantOption = /^options\[/i.test(name) || /-\d+$/.test(name);
    return isVariantOption && !COLOR_OPTION_NAME_RE.test(name);
  }

  function firstResolvableValue(nodes, maps) {
    var found = null;
    Array.prototype.forEach.call(nodes, function (node) {
      if (found) return;
      if (isOtherOptionControl(node)) return;
      var vals = candidateValuesFromNode(node);
      for (var i = 0; i < vals.length; i++) {
        if (resolveColor(vals[i], maps)) { found = vals[i]; break; }
      }
    });
    return found;
  }

  // Resolve the currently selected color in priority tiers. Genuine "checked"
  // signals win first; sticky presentation classes (.selected / .is-selected /
  // [data-selected]) are only a last resort, because a stale one can appear
  // earlier in the DOM than the truly-checked control and latch the color.
  function selectedColorValue(scope, maps) {
    var tiers = [
      'input[type="radio"]:checked, select option:checked, [aria-checked="true"], [aria-selected="true"]',
      'input[type="hidden"][name*="option" i]',
      '.is-selected, .selected, [data-selected="true"]'
    ];
    for (var t = 0; t < tiers.length; t++) {
      var v = firstResolvableValue(scope.querySelectorAll(tiers[t]), maps);
      if (v) return v;
    }
    return null;
  }

  // The control the shopper just interacted with is the most authoritative
  // source of truth (immune to stale/sticky selection markers elsewhere).
  function colorValueFromEventTarget(target, maps) {
    if (!target || !target.closest) return null;
    var node =
      target.closest(
        'input[type="radio"], option, label, button, [role="radio"], [data-value], [data-option-value]'
      ) || target;
    if (isOtherOptionControl(target) || isOtherOptionControl(node)) return null;
    // A clicked <label> may only carry the color via its associated input.
    var forId = node.getAttribute && node.getAttribute('for');
    var input = forId ? document.getElementById(forId) : null;
    if (input && isOtherOptionControl(input)) return null;
    var v = bestColorValueForNode(node, maps);
    if (v) return v;
    if (input) return bestColorValueForNode(input, maps);
    return null;
  }

  /* ---- Color randomize (select a random variant) -------------------------- */

  function bestColorValueForNode(node, maps) {
    var vals = candidateValuesFromNode(node);
    for (var i = 0; i < vals.length; i++) {
      if (resolveColor(vals[i], maps)) return vals[i];
    }
    return null;
  }

  function fireChange(el) {
    ['input', 'change'].forEach(function (type) {
      var ev;
      try {
        ev = new Event(type, { bubbles: true });
      } catch (e) {
        ev = document.createEvent('Event');
        ev.initEvent(type, true, false);
      }
      el.dispatchEvent(ev);
    });
  }

  function randomOf(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  // Select a random *color* variant in-place (so price / URL / add-to-cart all
  // update via the theme's own variant logic). Returns true when it acted.
  // Deliberately avoids <a> swatches to prevent navigation-reload loops.
  function randomizeColorSelection(scope, maps, log) {
    // A. Radio groups (Dawn variant-radios and most modern themes).
    var radios = Array.prototype.slice.call(scope.querySelectorAll('input[type="radio"]'));
    var groups = {};
    radios.forEach(function (r) {
      var key = r.name || '_';
      (groups[key] = groups[key] || []).push(r);
    });
    var bestGroup = null;
    Object.keys(groups).forEach(function (name) {
      var colorRadios = groups[name].filter(function (r) {
        return !isOtherOptionControl(r) && bestColorValueForNode(r, maps);
      });
      if (colorRadios.length >= 2 && (!bestGroup || colorRadios.length > bestGroup.length)) {
        bestGroup = colorRadios;
      }
    });
    if (bestGroup) {
      var pick = randomOf(bestGroup);
      if (pick) {
        if (!pick.checked) pick.checked = true;
        if (pick.click) { try { pick.click(); } catch (e) {} }
        fireChange(pick);
        log('randomized color (radio)', bestColorValueForNode(pick, maps));
        return true;
      }
    }

    // B. <select> whose options resolve to catalog colors.
    var selects = Array.prototype.slice.call(scope.querySelectorAll('select'));
    for (var s = 0; s < selects.length; s++) {
      if (isOtherOptionControl(selects[s])) continue;
      var opts = Array.prototype.slice.call(selects[s].options).filter(function (o) {
        return bestColorValueForNode(o, maps);
      });
      if (opts.length >= 2) {
        var opt = randomOf(opts);
        selects[s].value = opt.value;
        fireChange(selects[s]);
        log('randomized color (select)', bestColorValueForNode(opt, maps));
        return true;
      }
    }

    // C. Swatch buttons (never anchors - those would navigate and loop).
    var buttons = Array.prototype.slice
      .call(scope.querySelectorAll('button, [role="radio"], label[data-value], [data-option-value]'))
      .filter(function (el) {
        return el.tagName !== 'A' && bestColorValueForNode(el, maps);
      });
    if (buttons.length >= 2) {
      var btn = randomOf(buttons);
      try { btn.click(); } catch (e) {}
      log('randomized color (button)', bestColorValueForNode(btn, maps));
      return true;
    }

    log('randomize color: no in-place color control found (skipped).');
    return false;
  }

  // Every catalog-resolvable color offered by this product's swatches.
  function colorValuesInScope(scope, maps) {
    var nodes = scope.querySelectorAll(
      'input[type="radio"], select option, [role="radio"], [data-value], [data-option-value], button'
    );
    var out = [];
    var seen = {};
    Array.prototype.forEach.call(nodes, function (node) {
      if (isOtherOptionControl(node)) return;
      var v = bestColorValueForNode(node, maps);
      if (!v) return;
      var c = resolveColor(v, maps);
      if (c && c.hex && !seen[c.hex]) {
        seen[c.hex] = true;
        out.push(c);
      }
    });
    return out;
  }

  // Colors whose tag OR family matches a single normalized keyword. Prefer
  // color-word tags (accurate): chromatic colors carry a tag like "blue"/"pink";
  // neutrals (grey/white/beige/black/brown) do not, so fall back to family.
  function matchGroupKeyword(norm, colors) {
    var byTag = [];
    var byFamily = [];
    (colors || []).forEach(function (c) {
      if (!c || !c.hex) return;
      var tags = (c.tags || []).map(function (t) { return String(t).toLowerCase(); });
      if (tags.indexOf(norm) !== -1) byTag.push(c);
      else if (String(c.family || '').toLowerCase().indexOf(norm) !== -1) byFamily.push(c);
    });
    return byTag.length ? byTag : byFamily;
  }

  // Catalog colors this product actually offers (from data-product-colors on cards).
  // Empty when unset (PDP) — callers fall back to the full catalog.
  function offeredColorsForRoot(root, maps) {
    var csv = root && root.getAttribute ? root.getAttribute('data-product-colors') : '';
    var names = parseSlugs(csv);
    if (!names.length) return [];
    var out = [];
    var seen = {};
    names.forEach(function (name) {
      var c = resolveColor(name, maps);
      if (c && c.hex && !seen[c.hex]) {
        seen[c.hex] = true;
        out.push(c);
      }
    });
    return out;
  }

  // Resolve a set of catalog colors for one or more filter values. A value can
  // be a specific color ("Ocean Blue") or a color-group keyword ("blue",
  // "grey", "pink"). Group keywords match a color's tags or family; handle /
  // multi-word values ("light-blue") fall back to their individual words.
  function colorsForGroup(groupCsv, colors, maps) {
    var groups = parseSlugs(groupCsv).map(function (s) { return s.toLowerCase(); });
    if (!groups.length) return [];
    var out = [];
    var seen = {};
    function add(c) {
      if (c && c.hex && !seen[c.hex]) { seen[c.hex] = true; out.push(c); }
    }
    groups.forEach(function (g) {
      // Normalize ("gray" -> "grey" to match family names like "cool-grey").
      var norm = g.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\bgray\b/g, 'grey');
      var matches = matchGroupKeyword(norm, colors);
      // Handle / multi-word filter values ("light blue", "navy blue"): try each
      // word (last first, e.g. "light blue" -> "blue").
      if (!matches.length && norm.indexOf(' ') !== -1) {
        var toks = norm.split(' ');
        for (var i = toks.length - 1; i >= 0 && !matches.length; i--) {
          if (toks[i]) matches = matchGroupKeyword(toks[i], colors);
        }
      }
      if (matches.length) {
        matches.forEach(add);
        return;
      }
      // Not a group keyword: treat as a specific color ("Ocean Blue").
      var exact = resolveColor(g, maps);
      if (exact && exact.hex) add(exact);
    });
    return out;
  }

  // Detect an active color filter from the listing URL, e.g.
  //   ?filter.v.option.color=Blue
  //   ?filter.p.m.shopify.color-pattern=blue&filter.p.m.shopify.color-pattern=navy
  // Returns a CSV of value labels. Used as a fallback when the server-rendered
  // data-color-group is empty (the card snippet renders in an isolated `render`
  // scope where the `collection` / `search` objects aren't available).
  function colorGroupFromUrl() {
    var search = '';
    try { search = window.location.search || ''; } catch (e) { return ''; }
    if (!search) return '';
    var vals = [];
    search.replace(/^\?/, '').split('&').forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf('=');
      if (eq === -1) return;
      var key, val;
      try {
        key = decodeURIComponent(pair.slice(0, eq).replace(/\+/g, ' ')).toLowerCase();
        val = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' ')).trim();
      } catch (e) { return; }
      if (!val) return;
      // Shopify facet params are filter.*; keep the color-related ones.
      if (key.indexOf('filter.') === 0 && /colou?r/.test(key)) vals.push(val);
    });
    return vals.join(',');
  }

  // Pre-generate tinted derivatives for every *other* product color so switching
  // is instant (Cloudinary caches after the first request). The initial variant
  // is already server-rendered; skip it and stagger the rest after load.
  function prewarmColors(scope, maps, layers, opts, log, initialColorName) {
    var colors = colorValuesInScope(scope, maps);
    if (!colors.length) return;

    var initial = initialColorName ? resolveColor(initialColorName, maps) : null;
    var others = colors.filter(function (c) {
      return !initial || c.hex !== initial.hex;
    });
    if (!others.length) return;

    log('prewarming', others.length, 'other colors');
    var idx = 0;
    function warmOne(color) {
      Array.prototype.forEach.call(layers, function (img) {
        var template = (img.getAttribute('data-variant-template') || '').trim();
        if (template) {
          var url = partUrl(img, color, opts);
          if (url) {
            var im = new Image();
            im.decoding = 'async';
            im.src = url;
          }
          return;
        }
        var master = img.getAttribute('data-master');
        if (!master) return;
        var warmCard =
          !!(opts && opts.card) ||
          isCardComposerRoot(composerRootForNode(img));
        if ((opts.recolorMode || 'luminance') === 'cloudinary') {
          var curl = tintUrl(master, color.hex, opts.intensity, opts.grayscale, {
            card: warmCard,
          });
          if (curl) {
            var cim = new Image();
            cim.decoding = 'async';
            cim.src = curl;
          }
          return;
        }
        // Prime RECOLOR_CACHE only — do not swap the visible layer src.
        luminanceRecolorToDataUrl(
          masterDeliveryUrl(master, { card: warmCard }),
          color.hex,
          function () {}
        );
      });
    }
    function next() {
      if (idx >= others.length) return;
      warmOne(others[idx++]);
      setTimeout(next, 120);
    }
    var start = function () { setTimeout(next, 400); };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 1500 });
    } else {
      setTimeout(start, 800);
    }
  }

  /* ---- Model gender guard ------------------------------------------------- */

  var OC_WOMEN_SLUGS = 'aino,bailey,cecilia,emilie,freya,ksenia,lucia,mei,niamh,hafsatu,priya,rima,oko,yoki,zofia'.split(',');
  var OC_MEN_SLUGS = 'einar,james,ananda,paolo,lars,florian,thiago,minho,nikolai,diallo,karim,william,selam,chaska,ivan'.split(',');
  var OC_PARTNER_MAP = {
    aino: 'einar', bailey: 'james', cecilia: 'ananda', emilie: 'paolo', freya: 'lars',
    ksenia: 'florian', lucia: 'thiago', mei: 'minho', niamh: 'nikolai', hafsatu: 'diallo',
    priya: 'karim', rima: 'william', oko: 'selam', yoki: 'chaska', zofia: 'ivan'
  };
  var OC_WOMEN_VER = 'v1783165764';
  var OC_MEN_VER = 'v1783663205';

  var OC_MEN_SLUGS_CSV = OC_MEN_SLUGS.join(',');
  var OC_WOMEN_SLUGS_CSV = OC_WOMEN_SLUGS.join(',');

  function productHandleFromLocation() {
    var m = (location.pathname || '').match(/\/products\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]).replace(/\+/g, ' ').toLowerCase() : '';
  }

  function composerHandle(root) {
    var fromAttr = root ? root.getAttribute('data-oc-handle') : '';
    return String(fromAttr || productHandleFromLocation() || '').toLowerCase();
  }

  function isMensHandle(handle) {
    if (!handle) return false;
    return /pulse|(?:^|-)mens(?:-|$)|mens-|blaze-pants|bliss-t-shirt|spark-pants|-for-men|for-men-/.test(handle);
  }

  function isMensComposer(root) {
    if (!root) return false;
    if (root.getAttribute('data-oc-is-mens') === 'true') return true;
    if (root.getAttribute('data-oc-target-gender') === 'male') return true;
    return isMensHandle(composerHandle(root));
  }

  function isWomensComposer(root) {
    if (!root || isMensComposer(root)) return false;
    if (root.getAttribute('data-oc-is-mens') === 'false' && root.getAttribute('data-oc-target-gender') === 'female') {
      return true;
    }
    return root.getAttribute('data-oc-target-gender') === 'female';
  }

  function slugInList(slug, list) {
    return list.indexOf(slug) !== -1;
  }

  // Locale-safe guard: correct model pool when translated metafields disagree with handle.
  function enforceModelGender(root) {
    var img = root.querySelector('[data-outfit-model]');
    if (!img) return;

    var isMens = isMensComposer(root);
    var isWomens = !isMens && isWomensComposer(root);
    if (!isMens && !isWomens) return;

    var menSlugs = OC_MEN_SLUGS;
    var womenSlugs = OC_WOMEN_SLUGS;
    var targetSlugs = isMens ? menSlugs : womenSlugs;
    var targetVer = isMens ? OC_MEN_VER : OC_WOMEN_VER;
    var wrongVer = isMens ? OC_WOMEN_VER : OC_MEN_VER;
    var partnerMap = isMens ? OC_PARTNER_MAP : invertPartnerMap(OC_PARTNER_MAP);

    var template = root.getAttribute('data-model-template') || '';
    if (!template || template.indexOf(targetVer) === -1) {
      template = template.replace(wrongVer, targetVer);
      if (template.indexOf(targetVer) === -1) {
        template = 'https://res.cloudinary.com/dkzv7ze02/image/upload/' + targetVer + '/{modelSlug}.png';
      }
    }

    var src = img.getAttribute('src') || '';
    var slug = root.getAttribute('data-chosen-model') || '';
    var m = src.match(/\/([a-z]+)\.png(?:\?|$)/i);
    if (m) slug = m[1].toLowerCase();

    if (src.indexOf('/' + targetVer + '/') !== -1 && slugInList(slug, targetSlugs)) {
      return;
    }

    var wrongSlugs = isMens ? OC_WOMEN_SLUGS : OC_MEN_SLUGS;
    if (slugInList(slug, wrongSlugs)) {
      slug = partnerMap[slug] || '';
    }
    if (!slug || !slugInList(slug, targetSlugs)) {
      var seed = parseInt(root.getAttribute('data-color-seed'), 10);
      if (isNaN(seed) || seed < 0) seed = 0;
      slug = targetSlugs[seed % targetSlugs.length];
    }

    if (slug) {
      img.src = template.replace('{modelSlug}', slug);
      root.setAttribute('data-chosen-model', slug);
      root.setAttribute('data-model-template', template);
      root.setAttribute('data-model-slugs', isMens ? OC_MEN_SLUGS_CSV : OC_WOMEN_SLUGS_CSV);
      root.setAttribute('data-oc-target-gender', isMens ? 'male' : 'female');
      root.setAttribute('data-oc-is-mens', isMens ? 'true' : 'false');
    }
  }

  function invertPartnerMap(map) {
    var out = {};
    Object.keys(map).forEach(function (k) {
      out[map[k]] = k;
    });
    return out;
  }

  /* ---- Model randomize ---------------------------------------------------- */

  function initModelRandomize(root) {
    if (!toBool(root.getAttribute('data-randomize'))) return;
    if (toBool(root.getAttribute('data-model-locked'))) return;

    var template = root.getAttribute('data-model-template') || '';
    var slugs = parseSlugs(root.getAttribute('data-model-slugs'));
    if (!template || !slugs.length) return;

    var img = root.querySelector('[data-outfit-model]');
    if (!img) return;
    var slug = slugs[Math.floor(Math.random() * slugs.length)];
    if (slug) img.src = template.replace('{modelSlug}', slug);
  }

  /* ---- Model picker (display only) ---------------------------------------- */

  // Let shoppers view the outfit on a different model. Purely visual: swaps the
  // model layer's image; never touches variants / cart. Controls live next to
  // the composer (marked [data-oc-composer-scope]) as buttons [data-oc-model].
  function initModelPicker(root) {
    var template = root.getAttribute('data-model-template') || '';
    if (!template) return;

    var img = root.querySelector('[data-outfit-model]');
    if (!img) return;

    var scope = (root.closest && root.closest('[data-oc-composer-scope]')) || root.parentNode || document;

    function setModel(slug) {
      if (slug) img.src = template.replace('{modelSlug}', slug);
    }

    function cap(s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    }

    // Chip + pop-up menu overlaid on the image (preferred UI).
    var chip = scope.querySelector('[data-oc-modelchip]');
    if (chip) {
      var toggle = chip.querySelector('[data-oc-modelchip-toggle]');
      var menu = chip.querySelector('[data-oc-modelchip-menu]');
      var current = chip.querySelector('[data-oc-modelchip-current]');
      var items = chip.querySelectorAll('[data-oc-model]');

      var closeChip = function () {
        chip.classList.remove('is-open');
        if (menu) menu.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      };
      var openChip = function () {
        chip.classList.add('is-open');
        if (menu) menu.hidden = false;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      };

      if (toggle) {
        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          if (chip.classList.contains('is-open')) closeChip(); else openChip();
        });
      }

      Array.prototype.forEach.call(items, function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          var slug = b.getAttribute('data-oc-model');
          setModel(slug);
          if (current) current.textContent = cap(slug);
          Array.prototype.forEach.call(items, function (other) {
            var active = other === b;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-checked', active ? 'true' : 'false');
          });
          closeChip();
        });
      });

      document.addEventListener('click', function (e) {
        if (chip.classList.contains('is-open') && !chip.contains(e.target)) closeChip();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) closeChip();
      });
      return;
    }

    // Dropdown control (backwards compatibility).
    var select = scope.querySelector('[data-oc-model-select]');
    if (select) {
      select.addEventListener('change', function () { setModel(select.value); });
    }

    // Optional thumbnail / button control (kept for backwards compatibility).
    var buttons = scope.querySelectorAll('[data-oc-model]');
    Array.prototype.forEach.call(buttons, function (b) {
      b.addEventListener('click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        setModel(b.getAttribute('data-oc-model'));
        Array.prototype.forEach.call(buttons, function (other) {
          var active = other === b;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
    });
  }

  /* ---- Variant color ------------------------------------------------------ */

  function applyFixedHexLayers(root, opts, done) {
    var fixed = root.querySelectorAll('[data-fixed-hex][data-master]');
    var list = Array.prototype.slice.call(fixed || []);
    if (!list.length) {
      if (done) done();
      return;
    }
    var card = isCardComposerRoot(root);
    var pending = list.length;
    function settle() {
      pending--;
      if (pending <= 0 && done) done();
    }
    list.forEach(function (img) {
      var hex = img.getAttribute('data-fixed-hex');
      var master = img.getAttribute('data-master');
      if (!hex || !master) {
        settle();
        return;
      }
      if ((opts.recolorMode || 'luminance') === 'cloudinary') {
        var curl = tintUrl(master, hex, opts.intensity, opts.grayscale, {
          card: card,
        });
        if (curl) img.src = curl;
        settle();
        return;
      }
      luminanceRecolorToDataUrl(
        masterDeliveryUrl(master, { card: card }),
        hex.indexOf('#') === 0 ? hex : '#' + hex,
        function (dataUrl) {
          if (dataUrl) img.src = dataUrl;
          else {
            var fallback = tintUrl(master, hex, opts.intensity, opts.grayscale, {
              card: card,
            });
            if (fallback) img.src = fallback;
          }
          settle();
        }
      );
    });
  }

  function recolorCompanionLayers(root, productColor, complements, opts, done) {
    var comps = root.querySelectorAll('[data-oc-companion][data-master], [data-fixed-hex][data-master]');
    var list = Array.prototype.slice.call(comps || []).filter(function (img) {
      return img.hasAttribute('data-oc-companion') || img.hasAttribute('data-fixed-hex');
    });
    // Prefer explicit companion markers; fall back to all fixed-hex if none marked.
    var marked = list.filter(function (img) {
      return img.hasAttribute('data-oc-companion');
    });
    if (marked.length) list = marked;
    if (!list.length) {
      if (done) done();
      return;
    }
    // One hex per companion group so shorts body + elastic (PS40/WB65), or
    // Poise body + neckline, stay the same fabric color. Independent random
    // picks made the waistband drift to a different complement.
    var jeansHex = null;
    var otherHex = null;
    list.forEach(function (img) {
      var preferJeans = companionLayerIsJeans(img);
      var picked;
      if (preferJeans) {
        if (!jeansHex) jeansHex = pickCompanionHexForProduct(productColor, complements, true);
        picked = jeansHex;
      } else {
        if (!otherHex) otherHex = pickCompanionHexForProduct(productColor, complements, false);
        picked = otherHex;
      }
      if (picked) img.setAttribute('data-fixed-hex', picked);
    });
    applyFixedHexLayers(root, opts, done);
  }

  function initVariantColor(root) {
    if (!toBool(root.getAttribute('data-variant-color'))) return;

    var layers = root.querySelectorAll('[data-variant-tint]:not([data-fixed-hex])');
    var hasFixed = !!root.querySelector('[data-fixed-hex][data-master]');
    if (!layers.length && !hasFixed) {
      scheduleReveal(root);
      return;
    }

    var debug = toBool(root.getAttribute('data-debug'));
    function log() {
      if (debug && window.console) console.log.apply(console, ['[outfit-composer]'].concat([].slice.call(arguments)));
    }

    Promise.all([getCatalogColors(root), getCompanionComplements(root)]).then(function (results) {
      var colors = results[0];
      var complements = results[1];
      var recolorMode = (root.getAttribute('data-recolor-mode') || 'luminance').toLowerCase();
      if (recolorMode !== 'cloudinary' && recolorMode !== 'luminance') recolorMode = 'luminance';
      var opts = {
        intensity: root.getAttribute('data-tint-intensity') || '100',
        grayscale: toBool(root.getAttribute('data-tint-grayscale')),
        recolorMode: recolorMode
      };

      if (!colors || !colors.length) {
        log('no catalog colors found (inline #AbujaPaletteCatalog missing and no data-catalog-url).');
        // Still recolor any fixed-hex layers so cards aren't stuck on raw masters.
        applyFixedHexLayers(root, opts, function () { scheduleReveal(root); });
        return;
      }
      var maps = buildColorMaps(colors);

      var lastHex = null;
      var scope = scopeFor(root);
      // Listing cards (no live picker) set data-oc-context="card".
      var isCard = root.getAttribute('data-oc-context') === 'card';
      var randomizeColor = toBool(root.getAttribute('data-randomize-color'));

      // When a color filter is active on a listing card, restrict the card's
      // default color to that group (e.g. filter "Blue" -> a blue swatch).
      // Pool is this product's offered colors only — never any Abuja shade that
      // is not a Color option on the product.
      var groupInitial = null;
      var groupCsv = root.getAttribute('data-color-group');
      // Fallback: the card snippet can't read collection/search filters inside
      // its `render` scope, so derive the active color filter from the URL.
      if (!groupCsv) groupCsv = colorGroupFromUrl();
      var offered = offeredColorsForRoot(root, maps);
      var colorPool = offered.length ? offered : colors;
      if (groupCsv) {
        var candidates = colorsForGroup(groupCsv, colorPool, maps);
        if (candidates.length) {
          var gIdx = randomizeColor
            ? Math.floor(Math.random() * candidates.length)
            : (function () {
                var seed = parseInt(root.getAttribute('data-color-seed'), 10);
                if (isNaN(seed) || seed < 0) seed = Math.floor(Math.random() * candidates.length);
                return seed % candidates.length;
              })();
          groupInitial = candidates[gIdx].name;
          log('color group', groupCsv, '->', groupInitial, '(', candidates.length, 'of', colorPool.length, 'offered )');
        }
      }
      // Cards: random among offered colors when data-randomize-color; else seed.
      var cardInitial = null;
      if (isCard && offered.length) {
        if (randomizeColor) {
          cardInitial = offered[Math.floor(Math.random() * offered.length)].name;
        } else {
          var seedOffered = parseInt(root.getAttribute('data-color-seed'), 10);
          if (isNaN(seedOffered) || seedOffered < 0) {
            seedOffered = Math.floor(Math.random() * offered.length);
          }
          cardInitial = offered[seedOffered % offered.length].name;
        }
      }

      function apply(reason, evt) {
        // 1) Trust the control the shopper just touched. 2) Fall back to a
        // tiered scan of the current selection. 3) Group-restricted default
        // (filtered listing cards). 4) Offered product color / server initial.
        var fromEvent = evt && evt.target ? colorValueFromEventTarget(evt.target, maps) : null;
        var value =
          fromEvent ||
          (isCard ? groupInitial : null) ||
          selectedColorValue(scope, maps) ||
          groupInitial ||
          (isCard ? cardInitial : null) ||
          root.getAttribute('data-initial-color') ||
          '';
        var color = resolveColor(value, maps);
        if (!color) {
          log('unresolved color', { reason: reason, value: value });
          if (reason === 'init') {
            applyFixedHexLayers(root, opts, function () { scheduleReveal(root); });
          }
          return;
        }
        // Guard on the resolved hex (not the raw label) so distinct labels that
        // map to the same color don't thrash, but any real color change applies.
        if (color.hex === lastHex) {
          if (reason === 'init') scheduleReveal(root);
          return;
        }
        lastHex = color.hex;
        log('applying', { reason: reason, value: value, hex: color.hex, recolorMode: opts.recolorMode });
        applyColorToLayers(layers, color, opts, function () {
          recolorCompanionLayers(root, color, complements, opts, function () {
            if (reason === 'init') scheduleReveal(root);
          });
        });
      }

      // Optionally pick a random color variant on load (drives the real PDP).
      if (randomizeColor && scope && scope.querySelectorAll && !isCard) {
        randomizeColorSelection(scope, maps, log);
      }

      // Reveal runs inside apply() once luminance recolor (async) finishes.
      apply('init');

      // Listing cards never have a live variant picker — skip document-wide
      // listeners that would multiply per card and thrash on any DOM mutation.
      if (isCard) return;

      // 1. Native form events (radios / selects that emit change or input).
      if (scope && scope.addEventListener) {
        scope.addEventListener('change', function (e) { apply('change', e); });
        scope.addEventListener('input', function (e) { apply('input', e); });
        // Clicks on label/button swatches that don't emit change synchronously.
        scope.addEventListener('click', function (e) { apply('click', e); });
      }

      // 2. Common theme custom events (bubble to document).
      ['variant:change', 'on:variant:change', 'variantChange', 'product:variant-change'].forEach(
        function (name) {
          document.addEventListener(name, function () { apply(name); });
        }
      );

      // 3. Dawn-style pub/sub.
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        try { subscribe(PUB_SUB_EVENTS.variantChange, function () { apply('pubsub'); }); } catch (e) {}
      }

      // 4. Catch-all: themes that swap swatches via JS without a native event.
      if (typeof MutationObserver === 'function' && scope && scope.nodeType === 1) {
        var pending = null;
        var observer = new MutationObserver(function () {
          if (pending) return;
          pending = setTimeout(function () { pending = null; apply('mutation'); }, 60);
        });
        try {
          observer.observe(scope, {
            subtree: true,
            attributes: true,
            attributeFilter: ['checked', 'aria-checked', 'aria-selected', 'selected', 'class', 'value', 'data-selected']
          });
        } catch (e) {}
      }

      // Pre-warm other variant colors (unless disabled). Initial variant is skipped.
      if (root.getAttribute('data-prewarm') !== 'false') {
        prewarmColors(scope, maps, layers, opts, log, root.getAttribute('data-initial-color'));
      }

      root._outfitComposerApply = apply;
    });
  }

  /* ---- Init --------------------------------------------------------------- */

  // Match export collage_fill_alpha_scale / Calceta shell transparency_alpha.
  var DEFAULT_SHELL_ALPHA = 0.8;
  // Shell plate stems when outfit_parts lacks ``alpha`` (pre-alpha metafield exports).
  var SHELL_PLATE_RE = /(?:^|[_/])(?:SL037|OP040|XWB065|_FC034|_FC34|_FB34|TS082?_FB0?34|TS081?_FB0?34|waistband_outer)/i;

  function applyExportLayerAlphas(root) {
    if (!root || !root.querySelectorAll) return;
    var layers = root.querySelectorAll('img.oc-layer, img[data-variant-tint], img[data-fixed-hex], img[data-master]');
    Array.prototype.forEach.call(layers, function (img) {
      if (!img || img.hasAttribute('data-outfit-model') || img.hasAttribute('data-oc-bg')) return;
      var raw = img.getAttribute('data-alpha');
      var alpha = NaN;
      if (raw != null && String(raw).trim() !== '') {
        alpha = parseFloat(raw);
      }
      if (!(alpha >= 0 && alpha <= 1)) {
        // Fallback for older outfit_parts JSON without ``alpha``.
        var hint =
          (img.getAttribute('data-master') || '') +
          ' ' +
          (img.getAttribute('src') || '') +
          ' ' +
          (img.getAttribute('alt') || '');
        if (SHELL_PLATE_RE.test(hint)) alpha = DEFAULT_SHELL_ALPHA;
      }
      if (alpha >= 0 && alpha < 0.999) {
        img.style.opacity = String(alpha);
        if (!img.getAttribute('data-alpha')) img.setAttribute('data-alpha', String(alpha));
      }
    });
  }

  function enforceCompanionStack(root) {
    // Jeans / Glow pants → over product hem. Generic / Poise tops → under skirts/pants.
    // Backfills data-oc-companion on older outfit_parts that only use the label.
    if (!root || !root.querySelectorAll) return;
    var layers = root.querySelectorAll('img.oc-layer, img[data-master]');
    var untuckShirt = false;
    Array.prototype.forEach.call(layers, function (layer) {
      var layerHint =
        ((layer.getAttribute('data-master') || '') +
          ' ' +
          (layer.getAttribute('src') || '') +
          ' ' +
          (layer.getAttribute('alt') || '')).toUpperCase();
      if (/PART_TS086?_|TS086|_TS86_/.test(layerHint)) untuckShirt = true;
    });
    Array.prototype.forEach.call(layers, function (img) {
      if (!img || img.hasAttribute('data-outfit-model') || img.hasAttribute('data-oc-bg')) return;
      var hint =
        (img.getAttribute('data-master') || '') +
        ' ' +
        (img.getAttribute('src') || '') +
        ' ' +
        (img.getAttribute('alt') || '');
      var stack = (img.getAttribute('data-oc-companion') || '').trim().toLowerCase();
      if (stack !== 'over' && stack !== 'under') {
        if (/companion_bottom/i.test(hint) || /(?:^|\/)jeans(?:\.png)?(?:\?|$)/i.test(hint)) {
          stack = untuckShirt ? 'under' : 'over';
        } else if (/companion_top/i.test(hint)) {
          stack = 'under';
        } else if (
          /part_PS40\d?_|part_PS43\d?_|part_PS44\d?_|part_WB65\d?_/i.test(hint) &&
          img.hasAttribute('data-fixed-hex')
        ) {
          stack = untuckShirt ? 'under' : 'over';
        } else if (
          (/part_TS65\d?_|part_NL80\d?_/i.test(hint) ||
            /(?:^|\/)(?:generic[-_]?top|top)(?:\.png)?(?:\?|$)/i.test(hint)) &&
          img.hasAttribute('data-fixed-hex')
        ) {
          stack = 'under';
        } else {
          return;
        }
      }
      img.setAttribute('data-oc-companion', stack);
      img.style.zIndex = stack === 'over' ? '40' : '2';
      if (!img.getAttribute('data-fixed-hex') && img.getAttribute('data-master')) {
        // Stale metafields sometimes left companions on follow_variant — force fixed tint.
        img.setAttribute('data-fixed-hex', '3a3a3a');
        img.removeAttribute('data-variant-tint');
      }
    });
    if (!untuckShirt) return;
    // Shirt plates must sit above companion bottoms (z=2). Same-z DOM order
    // was painting pants over Joy / Delight.
    Array.prototype.forEach.call(layers, function (img) {
      if (!img || img.hasAttribute('data-outfit-model') || img.hasAttribute('data-oc-bg')) return;
      if ((img.getAttribute('data-oc-companion') || '').trim()) return;
      var hint =
        ((img.getAttribute('data-master') || '') +
          ' ' +
          (img.getAttribute('src') || '') +
          ' ' +
          (img.getAttribute('alt') || '')).toUpperCase();
      if (!/PART_(TS|NL|ST|OT|NC)\d/i.test(hint)) return;
      var z = parseInt(img.style.zIndex, 10);
      if (!(z >= 40)) img.style.zIndex = String(40 + (z > 0 ? z : 1));
    });
  }

  function initRoot(root) {
    if (!root || root._outfitComposerInit) return;
    root._outfitComposerInit = true;
    // Listing cards: show CDN masters immediately. Hiding until recolor finishes
    // leaves a blank gray Dawn "no media" card (no Shopify featured image).
    var isCard = root.getAttribute('data-oc-context') === 'card';
    var isComposerPage = root.getAttribute('data-oc-context') === 'composer';
    if (isCard || isComposerPage) {
      revealRoot(root);
    } else {
      root.classList.add('is-oc-loading');
      root.setAttribute('aria-hidden', 'true');
    }

    enforceModelGender(root);
    enforceCompanionStack(root);
    initModelRandomize(root);
    initModelPicker(root);
    applyExportLayerAlphas(root);

    var variantColor = toBool(root.getAttribute('data-variant-color'));
    var tintLayers = root.querySelector('[data-variant-tint], [data-fixed-hex]');
    if (variantColor && tintLayers) {
      initVariantColor(root);
    } else {
      scheduleReveal(root);
    }
  }

  function initAll(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll('[data-outfit-composer]');
    Array.prototype.forEach.call(roots, function (root) {
      if (!root._outfitComposerInit) initRoot(root);
    });
  }

  function initAddedNodes(nodes) {
    Array.prototype.forEach.call(nodes, function (node) {
      if (!node || node.nodeType !== 1) return;
      if (node.matches && node.matches('[data-outfit-composer]') && !node._outfitComposerInit) {
        initRoot(node);
        return;
      }
      if (node.querySelectorAll) {
        var roots = node.querySelectorAll('[data-outfit-composer]');
        Array.prototype.forEach.call(roots, function (root) {
          if (!root._outfitComposerInit) initRoot(root);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init when a section is (re)loaded in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });

  // Re-init composers injected without a full page load. Collection filtering
  // (Dawn facets, "load more", infinite scroll, etc.) replaces the product-grid
  // HTML in place and updates the URL, but fires neither DOMContentLoaded nor
  // shopify:section:load - so those new cards would otherwise never pick up the
  // active color filter. Watch for added composer roots and init them.
  function nodeHasComposer(n) {
    if (!n || n.nodeType !== 1) return false;
    if (n.matches && n.matches('[data-outfit-composer]')) return true;
    return !!(n.querySelector && n.querySelector('[data-outfit-composer]'));
  }
  if (typeof MutationObserver === 'function') {
    var reinitPending = null;
    var gridRoot =
      document.getElementById('ProductGridContainer') ||
      document.querySelector('.collection, .search');
    var observeTarget = gridRoot || document.documentElement;
    var observer = new MutationObserver(function (mutations) {
      if (reinitPending) return;
      var added = [];
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes || [];
        for (var j = 0; j < nodes.length; j++) {
          if (nodeHasComposer(nodes[j])) added.push(nodes[j]);
        }
      }
      if (!added.length) return;
      reinitPending = setTimeout(function () {
        reinitPending = null;
        initAddedNodes(added);
      }, 80);
    });
    try {
      observer.observe(observeTarget, { childList: true, subtree: true });
    } catch (e) {}
  }

  function applyLookColor(root, color, opts, done) {
    if (!root) {
      if (done) done();
      return;
    }
    var layerOpts = opts || {};
    var tintLayers = root.querySelectorAll('[data-variant-tint]:not([data-fixed-hex])');
    applyColorToLayers(tintLayers, color || {}, layerOpts, function () {
      applyFixedHexLayers(root, layerOpts, done);
    });
  }

  window.KailaOutfitComposer = {
    init: initAll,
    initRoot: initRoot,
    tintUrl: tintUrl,
    masterDeliveryUrl: masterDeliveryUrl,
    applyColorToLayers: applyColorToLayers,
    applyFixedHexLayers: applyFixedHexLayers,
    applyLookColor: applyLookColor,
    colorSlug: colorSlug,
    resolveColor: resolveColor
  };

  // Safety net: never leave composers invisible if image loads stall.
  setTimeout(function () {
    document.querySelectorAll('[data-outfit-composer].is-oc-loading').forEach(revealRoot);
  }, 8000);

  // ---------------------------------------------------------------------------
  // Conditional facets: hide Bottom Length unless Category is pants/skirt/dress.
  // Bundled here because collection pages already load this asset.
  // ---------------------------------------------------------------------------
  (function initConditionalBottomLengthFacet() {
    if (window.__kailaConditionalFacetsInit) return;
    window.__kailaConditionalFacetsInit = true;

    var CATEGORY_PARAM = 'filter.p.t.category';
    var BOTTOM_LENGTH_PARAM = 'filter.p.m.custom.bottom_length';
    var HIDDEN_CLASS = 'ayami-facet-hidden';
    var ALLOW_LABEL_RE =
      /\b(dress|dresses|skirt|skirts|pant|pants|trouser|trousers|legging|leggings)\b/i;
    var DENY_LABEL_RE =
      /\b(underpant|underpants|underwear|pantie|panties|brief|briefs)\b/i;

    function ensureStyle() {
      if (document.getElementById('ayami-conditional-facets-style')) return;
      var style = document.createElement('style');
      style.id = 'ayami-conditional-facets-style';
      style.textContent =
        '.' + HIDDEN_CLASS + '{display:none!important;visibility:hidden!important;}' +
        '.' + HIDDEN_CLASS + '[open]{display:none!important;}';
      (document.head || document.documentElement).appendChild(style);
    }

    function normalizeLabel(text) {
      return String(text || '')
        .replace(/\(\s*\d+\s*products?\s*\)/gi, ' ')
        .replace(/\(\s*\d+\s*\)/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function labelAllows(label) {
      var t = normalizeLabel(label);
      if (!t || DENY_LABEL_RE.test(t)) return false;
      return ALLOW_LABEL_RE.test(t);
    }

    function bottomLengthFilters() {
      var out = [];
      var seen = {};
      var nodes = document.querySelectorAll(
        'details.js-filter, .js-filter, details.facets__disclosure, details.mobile-facets__details'
      );
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (seen[el.id || i]) continue;
        var id = (el.id || '').toLowerCase();
        var summary = ((el.querySelector('summary') || {}).textContent || '').toLowerCase();
        var hit =
          id.indexOf('custom.bottom_length') !== -1 ||
          !!el.querySelector('input[name="' + BOTTOM_LENGTH_PARAM + '"]') ||
          summary.indexOf('bottom length') !== -1;
        if (!hit) continue;
        seen[el.id || i] = true;
        out.push(el);
      }
      return out;
    }

    function categoryLabel(input) {
      if (!input) return '';
      var lab = null;
      if (input.id) {
        try {
          lab = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        } catch (e) {
          lab = document.getElementById(input.id)
            ? document.querySelector('label[for="' + input.id + '"]')
            : null;
        }
      }
      if (!lab) lab = input.closest('label') || input.parentElement;
      return lab ? lab.textContent || '' : '';
    }

    function categoryAllows() {
      var inputs = document.querySelectorAll('input[name="' + CATEGORY_PARAM + '"]');
      var checked = [];
      var byValue = {};
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        byValue[String(input.value)] = categoryLabel(input);
        if (input.checked) checked.push(input);
      }
      if (checked.length) {
        for (var c = 0; c < checked.length; c++) {
          if (labelAllows(categoryLabel(checked[c]))) return true;
        }
        return false;
      }
      var urlValues = new URLSearchParams(window.location.search).getAll(CATEGORY_PARAM);
      if (!urlValues.length) return false;
      for (var j = 0; j < urlValues.length; j++) {
        if (labelAllows(byValue[String(urlValues[j])])) return true;
        var code = String(urlValues[j]).toLowerCase();
        if (
          code === 'aa-1-4' ||
          code === 'aa-1-15' ||
          code === 'aa-1-12-11' ||
          code === 'aa-1-25-1'
        ) {
          return true;
        }
      }
      return false;
    }

    function setVisible(el, visible) {
      ensureStyle();
      if (visible) {
        el.classList.remove(HIDDEN_CLASS);
        el.hidden = false;
        el.removeAttribute('aria-hidden');
        el.style.removeProperty('display');
      } else {
        el.classList.add(HIDDEN_CLASS);
        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        if (el.open) el.open = false;
      }
    }

    function clearBottomLengthUrl() {
      var params = new URLSearchParams(window.location.search);
      if (!params.has(BOTTOM_LENGTH_PARAM)) return false;
      var cleaned = new URLSearchParams();
      params.forEach(function (value, key) {
        if (key !== BOTTOM_LENGTH_PARAM) cleaned.append(key, value);
      });
      var qs = cleaned.toString();
      var next =
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.location.assign(next);
      return true;
    }

    function apply() {
      var els = bottomLengthFilters();
      if (!els.length) return;
      var show = categoryAllows();
      for (var i = 0; i < els.length; i++) setVisible(els[i], show);
      if (!show) {
        var boxes = document.querySelectorAll(
          'input[name="' + BOTTOM_LENGTH_PARAM + '"]'
        );
        for (var b = 0; b < boxes.length; b++) {
          if (boxes[b].checked) boxes[b].checked = false;
        }
        clearBottomLengthUrl();
      }
    }

    var pending = false;
    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        apply();
      });
    }

    function boot() {
      ensureStyle();
      apply();
      setTimeout(apply, 0);
      setTimeout(apply, 150);
      setTimeout(apply, 500);
      document.addEventListener(
        'change',
        function (e) {
          if (e.target && e.target.name && e.target.name.indexOf('filter.') === 0) {
            schedule();
          }
        },
        true
      );
      window.addEventListener('popstate', schedule);
      if (typeof MutationObserver === 'function') {
        var root =
          document.getElementById('ProductGridContainer') ||
          document.querySelector('facet-filters-form') ||
          document.body;
        try {
          new MutationObserver(schedule).observe(root, {
            childList: true,
            subtree: true,
          });
        } catch (err) {}
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  })();
})();
